// supervisor-outcome-router-done-path.ts
//
// The verified-done branch of supervisor-outcome-router.ts (§6.1 "done ->
// proposal-applier -> commit-owner -> ticket done"):
//   1. apply `proposed_changes` BEFORE committing — a stale
//      `expected_revision` fails the WHOLE done outcome (nothing committed);
//   2. commit via commit-owner; `policy_denied` escalates, never retried;
//   3. (review G3 H1) a touched ticket file missing at HEAD escalates.
// F-1 / 06d R2: `proposed_tickets`, a plan leaving no feature work, or plan
// acceptance delegating to the worker are refused before the commit. F-72: a
// refusal on the allow-list (done-path-worker-output-rejection.ts) is a
// COUNTED failure; every other refusal escalates (fail closed).

import type { AutopilotResult } from "./autopilot-result-schema.js";
import { commitTicket, type GitRunner } from "./commit-owner.js";
import {
  escalate,
  journalRejectionReceipt,
  PROPOSAL_APPLY_RECEIPT,
  rejectWorkerOutput,
} from "./done-path-worker-output-rejection.js";
import { readBoundBlueprintPath } from "./mission-blueprint-binding.js";
import type { MissionStore } from "./mission-store.js";
import {
  planDoneRejection,
  proposedTicketsRejectionReason,
} from "./plan-done-feature-ticket-guards.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import { batchGovernanceRefusal } from "./recovery-batch-governance-scope.js";
import type { RetryConfig, RetryDecision, RetryState } from "./retry-policy.js";
import { applyProposedChangesForAttempt } from "./supervisor-proposal-application.js";
import { loadTicketsFromRepo } from "./supervisor-ticket-projection.js";
import { touchedTicketsNotCommittedReason } from "./ticket-files-at-head-commit-check.js";

export interface DonePathInput {
  store: MissionStore;
  repoDir: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  manifestPaths: string[];
  missionId: string;
  runId: string;
  ticketId: string;
  ticketType: string;
  ticketScope: string;
  ticketSubject: string;
  ticketStatus: Record<string, string>;
  retryState: RetryState;
  /** F-72: a fixable rejection is a counted failure under this ceiling. */
  retryConfig: RetryConfig;
  now: Date;
  proposedChanges: AutopilotResult["proposed_changes"];
  /** F-1: never applied; non-empty ⇒ escalate (undefined ⇒ treated as `[]`). */
  proposedTickets?: readonly unknown[];
  /** R-25 (D2): DAG policy warnings (rules 3-4) from the proposal applier, plus (review 260930 L5) commit pathspecs that staged nothing — operator line, undefined ⇒ silent. */
  notify?: (line: string) => void;
  secretValues?: string[]; // 06d review L3: masks the R2 reason; undefined ⇒ patterns only
  /** Security audit M-2: holds `blueprint-binding.json` (the router passes it); undefined ⇒ default BLUEPRINT names only. */
  controlRoot?: string;
}

export type DonePathResult =
  | { kind: "done"; commitHeadSha: string | undefined }
  | { kind: "retry_decision"; decision: RetryDecision };

// Re-exported: the G0 claim path (and tests) import them from here.
export { escalate, journalRejectionReceipt };

/** 06d R2: not allow-listed ⇒ human-only; checked before any fixable refusal. */
const PLAN_DELEGATION_RECEIPT = "plan_delegates_open_decision_to_worker_rejected";

const applyFailed = (reason: string) =>
  `proposed_changes áp dụng thất bại, ticket KHÔNG được coi là done: ${reason}`;

export async function routeVerifiedDoneOutcome(input: DonePathInput): Promise<DonePathResult> {
  let manifestPaths = input.manifestPaths;
  // M1 lớp hai (code-reviewer 260922 C1). proposal-applier.ts ép ticket về
  // "todo" khi một amend đổi `acceptance[]` — đổi thước đo thì kết luận cũ hết
  // hiệu lực. Guard đó KHÔNG tự cứu được ca ticket bị ép lại chính là ticket
  // đang chạy: `journalAttemptOutcome` thấy route "done" nên commit tiếp
  // `ticket_state_changed -> done` SAU event "todo", và
  // mission-state-reducer.ts ghi đè không điều kiện ⇒ event sau thắng. Guard bị
  // vô hiệu ngay trong tick nó chạy, không test nào đỏ.
  //
  // Đọc THẲNG proposed_changes chứ không so statusMap trước/sau: `ticketStatus`
  // vào đây là projection đầu tick, nơi ticket ĐANG CHẠY vẫn mang "todo"
  // (supervisor-ticket-projection.ts:35), nên cả "đọc giá trị tuyệt đối" lẫn
  // "so trước/sau" đều sai — cái trước bắn cho MỌI attempt có proposed_changes
  // (e2e cancel-proposal rơi từ done xuống blocked), cái sau không bắn bao giờ.
  //
  // Escalate chứ không retry: worker vừa đổi thước đo ngay lúc tuyên đã đạt —
  // cho chạy tiếp bằng thước mới là để bên bị kiểm tự viết luật.
  const selfAmendsAcceptance = input.proposedChanges.some(
    (change) =>
      change.op === "amend" &&
      change.target === input.ticketId &&
      (change.content as { acceptance?: unknown } | undefined)?.acceptance !== undefined,
  );
  if (selfAmendsAcceptance) {
    await journalRejectionReceipt(input.store, "self_amended_acceptance_rejected");
    return escalate(
      input.retryState,
      `attempt sửa acceptance của chính ticket ${input.ticketId} trong lượt báo done — ` +
        "điều kiện nghiệm thu đổi thì kết luận done không còn hiệu lực, cần người duyệt.",
      undefined,
      "self_amend",
    );
  }
  // Audit F-1 ("no code wins"): every human-only check runs before any
  // fixable one — delegation (06d R2, spec E17 b), then governance over the
  // whole proposed_changes batch — so a violation is never masked by a retry.
  const needsTickets = input.ticketType === "plan" || input.proposedChanges.length > 0;
  const repoTickets = needsTickets ? await loadTicketsFromRepo(input.repoDir) : [];
  // F-1 R2 on the PROJECTED graph, before anything is applied (review 261010 M2).
  const planRejection =
    input.ticketType === "plan" ? planDoneRejection(input, repoTickets) : undefined;
  if (planRejection?.receipt === PLAN_DELEGATION_RECEIPT) {
    return rejectWorkerOutput(input, planRejection.receipt, planRejection.reason);
  }
  // M-2: the BLUEPRINT the mission bound at init, not only the default names.
  const blueprintPath = needsTickets ? await readBoundBlueprintPath(input.controlRoot) : undefined;
  const governance = batchGovernanceRefusal(repoTickets, {
    store: input.store,
    sourceTicketId: input.ticketId,
    proposedChanges: input.proposedChanges,
    blueprintPath,
    ...(input.secretValues === undefined ? {} : { secretValues: input.secretValues }),
  });
  if (governance !== undefined) {
    return rejectWorkerOutput(input, PROPOSAL_APPLY_RECEIPT, applyFailed(governance), {
      remoteId: governance,
    });
  }
  const ticketsReason = proposedTicketsRejectionReason(input.ticketId, input.proposedTickets ?? []);
  if (ticketsReason !== undefined) {
    return rejectWorkerOutput(input, "proposed_tickets_rejected", ticketsReason);
  }
  if (planRejection !== undefined) {
    return rejectWorkerOutput(input, planRejection.receipt, planRejection.reason);
  }
  let touchedTicketIds: string[] = [];
  if (input.proposedChanges.length > 0) {
    const applied = await applyProposedChangesForAttempt({
      store: input.store,
      repoDir: input.repoDir,
      runId: input.runId,
      ticketStatus: input.ticketStatus,
      proposedChanges: input.proposedChanges,
      sourceTicketId: input.ticketId, // 06e R3: a controller redirect decision may supersede a blocked plan
      blueprintPath,
      notify: input.notify,
    });
    if (!applied.ok) {
      const opts = { remoteId: applied.reason, code: applied.code };
      return rejectWorkerOutput(input, PROPOSAL_APPLY_RECEIPT, applyFailed(applied.reason), opts);
    }
    if (applied.touchedTicketIds.length > 0) {
      manifestPaths = [...input.manifestPaths, ".avatar/tickets", ".avatar/PLAN.md"]; // 06e R5
      touchedTicketIds = applied.touchedTicketIds;
    }
  }

  const commit = await commitTicket({
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    manifestPaths,
    missionId: input.missionId,
    runId: input.runId,
    ticketId: input.ticketId,
    type: input.ticketType,
    scope: input.ticketScope,
    subject: input.ticketSubject,
    notify: input.notify,
  });
  if (commit.status === "policy_denied") {
    // Governance denial on the supervisor's OWN commit (§5.4): not a worker
    // error category, so counted_failures does not apply — a human decides.
    await journalRejectionReceipt(input.store, "policy_denied");
    const reason = `commit bị policy từ chối: ${commit.reason}`;
    return escalate(input.retryState, reason, reason, "policy_deny");
  }
  // Review G3 H1: a ticket an ignore rule kept out of the commit is deleted by
  // the next reset — never report `done` over it; a person looks (uncounted).
  const notCommitted = await touchedTicketsNotCommittedReason(
    input.gitRunner,
    input.repoDir,
    touchedTicketIds,
  );
  if (notCommitted !== undefined) {
    await journalRejectionReceipt(input.store, "ticket_not_committed");
    return escalate(input.retryState, notCommitted);
  }
  return {
    kind: "done",
    commitHeadSha: commit.status === "committed" ? commit.headSha : commit.baseSha,
  };
}
